import { Typography, Box } from '@mui/material';
import { useDocumentContext } from './DocumentContext';
import { useCallback, useMemo } from 'react';
import TreeViewNavigation from '../features/TreeViewNavigation';
import type { TreeNode } from '../features/TreeViewNavigation/types';
import type { TOCNode } from '../types/DocumentTypes';
import { useUserRelatedDocumentContext } from './DocumentUserRelatedContext';

export default function TableOfContents() {
  const { state: documentState, chapters } = useDocumentContext();
  const { navigateToChapterByNodeId } = useUserRelatedDocumentContext();

  const goToSelectedChapter = useCallback(
    async (nodeId: number) => {
      if (!chapters || chapters.length === 0) {
        return;
      }
      await navigateToChapterByNodeId(nodeId);
    },
    [chapters, navigateToChapterByNodeId],
  );

  const treeNodes: TreeNode[] | null = useMemo(() => {
    const toTreeNode = (node: TOCNode): TreeNode => ({
      title: node.title,
      nodeId: node.nodeId,
      parentNodeId: node.parentNodeId,
      hasChildren: node.hasChildren,
      children: node.children ? node.children.map(toTreeNode) : null,
    });
    return chapters ? chapters.map(toTreeNode) : null;
  }, [chapters]);

  if (documentState === 'loading') {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='text.secondary'>
          Loading Table of Contents...
        </Typography>
      </Box>
    );
  }

  if (documentState === 'error') {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='error'>Failed to load Table of Contents.</Typography>
      </Box>
    );
  }

  if (!treeNodes || treeNodes.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='text.secondary'>
          Table of Contents not available.
        </Typography>
      </Box>
    );
  }

  return (
    <TreeViewNavigation
      label={'Table of Contents'}
      nodes={treeNodes}
      onSelect={goToSelectedChapter}
    />
  );
}
